import React, { useContext } from "react";
import { AuthContext } from "../contexts/AuthContext";
import Header from "../layout/Header/Header";

/**
 * MenuGrid — menú de módulo con tarjetas seccionadas.
 *
 * props:
 *  - title       : título del módulo (string)
 *  - subtitle    : descripción corta (string)
 *  - icon        : clase FontAwesome del encabezado (ej. "fas fa-box")
 *  - accent      : { color, bg } del encabezado
 *  - gate        : permiso de módulo; si el usuario no lo tiene, no se muestra nada
 *  - sections    : [{ title, color, bg, items: [{ label, icon, href, perm }] }]
 *
 * Cada item se habilita según user[perm]. Color/bg del item se heredan de su
 * sección salvo que el item los traiga propios.
 */

const PALETTE = [
  { color: "#10b981", bg: "#f0fdf4" }, // emerald
  { color: "#3b82f6", bg: "#eff6ff" }, // blue
  { color: "#f59e0b", bg: "#fffbeb" }, // amber
  { color: "#8b5cf6", bg: "#f5f3ff" }, // violet
  { color: "#0ea5e9", bg: "#f0f9ff" }, // sky
  { color: "#f43f5e", bg: "#fff1f2" }, // rose
  { color: "#14b8a6", bg: "#f0fdfa" }, // teal
  { color: "#6366f1", bg: "#eef2ff" }, // indigo
];

function Card({ label, icon, href, color, bg, enabled }) {
  return (
    <div className="col-6 col-md-4 col-xl-3 mb-3">
      <a
        href={enabled ? href : undefined}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
          padding: "22px 14px",
          background: "#fff",
          borderRadius: 10,
          boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
          border: "1px solid #f1f5f9",
          textDecoration: "none",
          color: "#334155",
          transition: "box-shadow 0.15s, transform 0.15s",
          textAlign: "center",
          height: "100%",
          opacity: enabled ? 1 : 0.45,
          pointerEvents: enabled ? "auto" : "none",
          cursor: enabled ? "pointer" : "default",
        }}
        onMouseEnter={(e) => {
          if (!enabled) return;
          e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.1)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.07)";
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: 12,
            background: bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <i className={icon} style={{ color, fontSize: "1.35rem" }} />
        </div>
        <div style={{ fontWeight: 600, fontSize: "0.82rem", lineHeight: 1.25 }}>
          {label}
        </div>
      </a>
    </div>
  );
}

function MenuGrid({ title, subtitle, icon, accent = {}, gate, sections = [] }) {
  const { user } = useContext(AuthContext);
  const headerColor = accent.color || "#3b82f6";
  const headerBg = accent.bg || "#eff6ff";

  return (
    <>
      <Header />
      <div className="card container col-12" style={{ padding: "20px 24px" }}>
        {/* Encabezado de módulo */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: headerBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i className={icon} style={{ color: headerColor, fontSize: "1.1rem" }} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 700, color: "#0f172a" }}>
              {title}
            </h3>
            {subtitle && (
              <p style={{ margin: 0, fontSize: "0.75rem", color: "#64748b" }}>{subtitle}</p>
            )}
          </div>
        </div>

        {!gate || user[gate] ? (
          sections.map((sec, si) => {
            const tone = PALETTE[si % PALETTE.length];
            const color = sec.color || tone.color;
            const bg = sec.bg || tone.bg;
            return (
              <div key={si} style={{ marginBottom: 18 }}>
                {sec.title && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      margin: "4px 0 12px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        color: "#475569",
                      }}
                    >
                      {sec.title}
                    </span>
                    <span style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
                  </div>
                )}
                <div className="row">
                  {sec.items.map((it, ii) => (
                    <Card
                      key={ii}
                      label={it.label}
                      icon={it.icon}
                      href={it.href}
                      color={it.color || color}
                      bg={it.bg || bg}
                      enabled={!!user[it.perm]}
                    />
                  ))}
                </div>
              </div>
            );
          })
        ) : (
          <p style={{ color: "#94a3b8", fontSize: "0.82rem" }}>Sin acceso a este módulo.</p>
        )}
      </div>
    </>
  );
}

export default MenuGrid;
